import type { AiRefusal } from '../ai/aiSeam';
import { t, type StringKey } from '../strings';
import { dealDiscoveryPath } from './discovery';
import { dealPackBaseName } from './dealPack';

/**
 * Ask Claude on the Discovery card (piece 19, *The Discovery card*; piece 21,
 * *The AI seam*; decision 5530, row T2780 batch 1). 🔒 The press sends the Deal
 * Pack without its Budget pair through the AI seam as the feature `askClaude`,
 * and the open questions Claude answers with come back onto the card as a list,
 * each with an answer box. Resubmit sends the answers and asks again.
 *
 * 🔒 Each question is a `Note` on the Deal whose section key is
 * `ask-claude:question`, and each answer a `Note` whose section key is
 * `ask-claude:answer:` and the question note's id. 🔨 A template's key is made of
 * lower-case letters, digits and hyphens, so a key holding a colon is never read
 * as a Discovery answer's `template/question`.
 */

/** 🔒 The feature the cost log names an Ask Claude call by (piece 21). */
export const ASK_CLAUDE_FEATURE = 'askClaude' as const;

/** 🔒 The section key every question note carries. */
export const ASK_CLAUDE_QUESTION_TAG = 'ask-claude:question';

/** The opening every answer note's section key carries, before the question note's id. */
export const ASK_CLAUDE_ANSWER_TAG_PREFIX = 'ask-claude:answer:';

/** The opening both kinds of Ask Claude note share, which the pack reads them by. */
export const ASK_CLAUDE_TAG_PREFIX = 'ask-claude:';

/** 🔒 An answer note's section key: the prefix and the question note's id. */
export function askClaudeAnswerTag(questionNoteId: string): string {
  return `${ASK_CLAUDE_ANSWER_TAG_PREFIX}${questionNoteId}`;
}

/** The question note an answer note's section key names, or null for any other key. */
export function askClaudeQuestionOfTag(key: string | null | undefined): string | null {
  if (!key || !key.startsWith(ASK_CLAUDE_ANSWER_TAG_PREFIX)) return null;
  const id = key.slice(ASK_CLAUDE_ANSWER_TAG_PREFIX.length);
  return id === '' ? null : id;
}

/**
 * 🔒 The application's own instruction to the model (decision 5530). The
 * proposal skill's own text is not in the repository and is not sent; how it
 * reaches the call is batch 2's, open under piece 21's Open issues.
 */
export const ASK_CLAUDE_INSTRUCTION =
  "Read the Deal pack below, which describes a customer's controls project. List the open questions a controls proposal for this project still needs answered, as a numbered list with one question per item, and nothing else. Leave out any question the pack already answers.";

/**
 * 🔒 Make Report's own instruction (decision 6002; the owner's answer D7: *"The
 * instructions are specific to what module or button is calling the claude"*).
 * It ships with the application beside Ask Claude's, never in a Settings box,
 * and the seam's registry names it for `makeReport` alone.
 */
export const MAKE_REPORT_INSTRUCTION =
  "Read the Deal pack below, which describes a customer's controls project and the questions answered at the site visit. Write the controls proposal report for this project in Markdown: the scope, the existing system, what is proposed, the assumptions made, and any question still open. Use only what the pack says, and say where it says nothing.";

/** 🔒 The feature the cost log names a Make Report call by (decision 6002). */
export const MAKE_REPORT_FEATURE = 'makeReport' as const;

/** 🔒 Make Report's fixed shape (decision 0202): one text value holding the report. */
export const MAKE_REPORT_SHAPE = { report: 'text' } as const;

/**
 * 🔒 The report is a Document of the Deal (decision 6003; the owner's answer D8):
 * a `File` tagged to the Deal under this seeded system type, which tags it as the
 * Deal's proposal report, and a second Make Report its next version.
 */
export const PROPOSAL_REPORT_FILE_TYPE_NAME = 'Proposal Report';

/** The report's name on the Deal, the Deal's own name and the words Proposal Report, in Markdown. */
export function proposalReportFileName(dealName: string): string {
  return `${dealPackBaseName(dealName)} Proposal Report.md`;
}

/**
 * 🔒 Ask Claude, Resubmit and Make Report are a permission a role carries
 * (decision 6004; the owner's answer D9: *"make this a permission item, in
 * settings or user permissions much like we limit views"*). A role without the
 * key is drawn none of the three, and their routes refuse it.
 */
export const ASK_CLAUDE_CAPABILITY = 'deals.askClaude';

/** 🔒 The reason Make Report's refusal names while questions stand open, which the card reads to ask its confirm. */
export const MAKE_REPORT_OPEN_QUESTIONS = 'openQuestions' as const;

/** 🔒 The one field the call sends: the pack's text, its Budget pair left out. */
export const ASK_CLAUDE_PACK_FIELD = 'dealPack' as const;

/** 🔒 The answer's fixed shape (decision 0202): one text value holding the numbered list. */
export const ASK_CLAUDE_SHAPE = { questions: 'text' } as const;

/** The most questions one answer is read for, and the longest a question is kept at. */
export const ASK_CLAUDE_QUESTIONS_MAX = 30;
export const ASK_CLAUDE_QUESTION_MAX_LENGTH = 500;
/** The longest answer an answer box takes. */
export const ASK_CLAUDE_ANSWER_MAX_LENGTH = 4000;

/** A numbered item's opening, *1.* or *1)*, and a bullet's. */
const NUMBERED = /^\s*\d+\s*[.)]\s+/;
const BULLET = /^\s*[-*•]\s+/;

/**
 * 🔒 The questions a numbered list holds, in its order (decision 0202: the text
 * is data, read as plain text and never as markup). A numbered line opens a
 * question and an unnumbered line after it continues that question; a list
 * with no numbers reads one question per line. Each question is cut at its
 * longest, a repeat is kept once, and the list is cut at its most.
 */
export function askClaudeQuestionsOf(text: string): string[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n').map((line) => line.trim()).filter((line) => line !== '');
  const numbered = lines.some((line) => NUMBERED.test(line));
  const items: string[] = [];
  for (const line of lines) {
    if (!numbered) items.push(line.replace(BULLET, ''));
    else if (NUMBERED.test(line)) items.push(line.replace(NUMBERED, ''));
    else if (items.length > 0) items[items.length - 1] = `${items[items.length - 1]} ${line}`;
  }
  const kept: string[] = [];
  for (const item of items) {
    const question = item.replace(/\s+/g, ' ').trim().slice(0, ASK_CLAUDE_QUESTION_MAX_LENGTH);
    if (question === '' || kept.some((held) => sameAskClaudeQuestion(held, question))) continue;
    kept.push(question);
    if (kept.length === ASK_CLAUDE_QUESTIONS_MAX) break;
  }
  return kept;
}

/** Two questions are the same when they read alike past case, spacing and the closing stop. */
export function sameAskClaudeQuestion(a: string, b: string): boolean {
  const said = (text: string) => text.toLowerCase().replace(/\s+/g, ' ').replace(/[\s?.!:]+$/, '').trim();
  return said(a) === said(b);
}

/**
 * 🔒 An answer note opens with its question on a line of its own, so the answer
 * reads the same wherever the note is read, as a Discovery answer opens with its
 * label (decision 5461).
 */
export function askClaudeAnswerText(question: string, value: string): string {
  return `${question.trim()}\n${value.trim()}`;
}

/** The value an answer note holds, its question line taken off where the note opens with it. */
export function askClaudeAnswerValue(question: string, text: string): string {
  const prefix = `${question.trim()}\n`;
  return text.startsWith(prefix) ? text.slice(prefix.length) : text;
}

// ---- Routes ----

/** Ask Claude and Resubmit: the answers given are written, then the pack is sent. Dismiss is this path's delete. */
export function dealAskClaudePath(dealId: string): string {
  return `${dealDiscoveryPath(dealId)}/ask-claude`;
}

/** 🔒 Make Report (decision 6003): the report is written, or its next version, from the pack and the answers. */
export function dealMakeReportPath(dealId: string): string {
  return `${dealDiscoveryPath(dealId)}/make-report`;
}

/** One answer, written as its tagged note when its box is left. */
export function dealAskClaudeAnswersPath(dealId: string): string {
  return `${dealAskClaudePath(dealId)}/answers`;
}

/** Clear Answer: this path's delete takes the question's answer notes off. */
export function dealAskClaudeAnswerPath(dealId: string, questionNoteId: string): string {
  return `${dealAskClaudeAnswersPath(dealId)}/${encodeURIComponent(questionNoteId)}`;
}

// ---- The card ----

/** One question on the card: its note, its words, and its newest answer. */
export interface AskClaudeQuestionView {
  /** The question note's id, which its answer notes name. */
  id: string;
  question: string;
  askedAt: string | null;
  /** The newest answer note's value; null while none is written. */
  answer: string | null;
  answeredBy: string | null;
  answeredAt: string | null;
}

/** A tagged note as the page's read hands it over, newest first under each section key. */
export interface AskClaudeNoteRead {
  id?: string | null;
  label: string;
  author?: string | null;
  at?: string | null;
}

/**
 * 🔒 The card's question list from the Deal's tagged notes, in the order they
 * were asked: each question note, and its newest answer note's value.
 */
export function askClaudeQuestionsFromNotes(notes: ReadonlyMap<string, readonly AskClaudeNoteRead[]>): AskClaudeQuestionView[] {
  const asked = [...(notes.get(ASK_CLAUDE_QUESTION_TAG) ?? [])].reverse();
  return asked.flatMap((note) => {
    if (!note.id) return [];
    const newest = notes.get(askClaudeAnswerTag(note.id))?.[0] ?? null;
    return [{
      id: note.id, question: note.label, askedAt: note.at ?? null,
      answer: newest ? askClaudeAnswerValue(note.label, newest.label) : null, answeredBy: newest?.author ?? null, answeredAt: newest?.at ?? null,
    }];
  });
}

/** 🔒 A question's label over its answer box, numbered from one in the order asked: *2. Which PLC runs it today?* */
export function askClaudeQuestionLabel(index: number, question: string): string {
  return `${index + 1}. ${question}`;
}

/** 🔒 The list's count line, *2 of 5 answered*. */
export function askClaudeCountLine(questions: readonly AskClaudeQuestionView[]): string {
  const answered = questions.filter((question) => question.answer !== null && question.answer.trim() !== '').length;
  return `${answered} ${t('discovery.of')} ${questions.length} ${t('askClaude.answered')}`;
}

/** 🔒 The questions on the card still open, which Make Report asks a confirm for (decision 6003). */
export function askClaudeOpenCount(questions: readonly AskClaudeQuestionView[]): number {
  return questions.filter((question) => question.answer === null || question.answer.trim() === '').length;
}

/** 🔒 The Deal's proposal report as the Discovery card names it: the Document, its newest version and when that was made. */
export interface DiscoveryReportView {
  fileId: string;
  filename: string;
  version: number;
  madeAt: string;
}

/** What Make Report answers: the Document written and the version it is now on. */
export interface MakeReportOutcome {
  fileId: string;
  version: number;
}

/** What Ask Claude and Resubmit answer: how many questions came back, how many were new and how many answered ones stayed. */
export interface AskClaudeOutcome {
  asked: number;
  added: number;
  kept: number;
}

/** 🔒 The reason the server names beside its refusal, which the web reads to offer Settings. */
export const ASK_CLAUDE_SEAM_OFF = 'seamOff' as const;

/** 🔒 Why nothing came back, in the application's words: one sentence per refusal of the seam (decision 0559, never silent). */
export function askClaudeRefusalKey(refusal: AiRefusal | 'noList'): StringKey {
  switch (refusal) {
    case 'inactive':
      return 'askClaude.refused.seamOff';
    case 'capReached':
      return 'askClaude.refused.capReached';
    case 'noKey':
    case 'refusedKey':
      return 'askClaude.refused.key';
    case 'rateLimited':
      return 'askClaude.refused.rateLimited';
    case 'noList':
      return 'askClaude.refused.noList';
    default:
      return 'askClaude.refused.unavailable';
  }
}

/**
 * 🔒 Make Report's refusals in the application's words (decision 6003): the seam off and the cap reached read as Ask
 * Claude's, and what Anthropic refused, an answer holding no report and questions still open name Make Report.
 */
export function makeReportRefusalKey(refusal: AiRefusal | 'noReport' | typeof MAKE_REPORT_OPEN_QUESTIONS): StringKey {
  switch (refusal) {
    case 'inactive':
    case 'capReached':
      return askClaudeRefusalKey(refusal);
    case 'noKey':
    case 'refusedKey':
      return 'makeReport.refused.key';
    case 'rateLimited':
      return 'makeReport.refused.rateLimited';
    case 'noReport':
      return 'makeReport.refused.noReport';
    case MAKE_REPORT_OPEN_QUESTIONS:
      return 'makeReport.refused.openQuestions';
    default:
      return 'makeReport.refused.unavailable';
  }
}
